import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  TableSortLabel,
  Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as AnalyzeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { stockService } from '../../services/stockService';

const getScreenerSymbol = (symbol: string): string => {
  return symbol.replace('.NS', '').replace('.BO', '');
};

interface MarketEvent {
  id: string;
  name: string;
  event_date: string;
  description: string;
  category: string;
  analysis_days_before: number;
  analysis_days_after: number;
  is_active: boolean;
  is_system_event: boolean;
  impact_count: number;
}

interface StockEventImpact {
  id: string;
  stock_id: string;
  symbol: string;
  stock_name: string;
  event_id: string;
  event_name: string;
  price_before_event: number;
  price_at_event: number;
  bottom_price: number;
  recovery_price: number;
  max_drawdown_pct: number;
  days_to_bottom: number;
  bottom_date: string;
  recovery_pct: number;
  days_to_recovery: number;
  full_recovery_date: string;
  recovered_to_baseline: boolean;
  volatility_during_event: number;
  resilience_score: number;
  opportunity_score: number;
}

interface AnalysisSummary {
  total_analyzed: number;
  avg_drawdown: number;
  worst_drawdown: number;
  best_drawdown: number;
  avg_resilience: number;
  recovered_count: number;
}

type SortField = 'resilience_score' | 'max_drawdown_pct' | 'days_to_recovery' | 'opportunity_score' | 'symbol';
type SortOrder = 'asc' | 'desc';

const EventImpactTab: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<MarketEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [impacts, setImpacts] = useState<StockEventImpact[]>([]);
  const [summary, setSummary] = useState<AnalysisSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Sorting
  const [sortField, setSortField] = useState<SortField>('resilience_score');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  
  // Add event dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    name: '',
    event_date: '',
    description: '',
    category: 'custom',
  });
  
  // Analysis status
  const [analysisStatus, setAnalysisStatus] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Load events on mount
  useEffect(() => {
    loadEvents();
  }, []);

  // Load analysis when event changes
  useEffect(() => {
    if (selectedEventId) {
      loadEventAnalysis();
      checkAnalysisStatus();
    }
  }, [selectedEventId, sortField, sortOrder]);

  const loadEvents = async () => {
    try {
      setLoadingEvents(true);
      const response = await stockService.getMarketEvents();
      setEvents(response.events || []);
      
      // Auto-select first event if available
      if (response.events?.length > 0 && !selectedEventId) {
        setSelectedEventId(response.events[0].id);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load events');
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadEventAnalysis = async () => {
    if (!selectedEventId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await stockService.getEventAnalysis(selectedEventId, {
        limit: 200,
        sort_by: sortField === 'symbol' ? 'resilience' : sortField.replace('_score', '').replace('_pct', ''),
        sort_order: sortOrder,
      });
      
      setImpacts(response.impacts || []);
      setSummary(response.summary);
    } catch (err: any) {
      setError(err.message || 'Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  const checkAnalysisStatus = async () => {
    if (!selectedEventId) return;
    
    try {
      const status = await stockService.getEventAnalysisStatus(selectedEventId);
      setAnalysisStatus(status);
    } catch (err) {
      console.error('Failed to check analysis status:', err);
    }
  };

  const handleTriggerAnalysis = async () => {
    if (!selectedEventId) return;
    
    try {
      setAnalyzing(true);
      await stockService.triggerEventAnalysis(selectedEventId);
      
      // Poll for status updates
      const pollInterval = setInterval(async () => {
        const status = await stockService.getEventAnalysisStatus(selectedEventId);
        setAnalysisStatus(status);
        
        if (!status.analysis_running) {
          clearInterval(pollInterval);
          setAnalyzing(false);
          loadEventAnalysis();
        }
      }, 5000);
      
    } catch (err: any) {
      setError(err.message || 'Failed to start analysis');
      setAnalyzing(false);
    }
  };

  const handlePrepopulateEvents = async () => {
    try {
      setLoading(true);
      await stockService.prepopulateEvents();
      loadEvents();
    } catch (err: any) {
      setError(err.message || 'Failed to prepopulate events');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async () => {
    try {
      await stockService.createMarketEvent(newEvent);
      setAddDialogOpen(false);
      setNewEvent({ name: '', event_date: '', description: '', category: 'custom' });
      loadEvents();
    } catch (err: any) {
      setError(err.message || 'Failed to create event');
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await stockService.deleteMarketEvent(eventId);
      loadEvents();
      if (selectedEventId === eventId) {
        setSelectedEventId('');
        setImpacts([]);
        setSummary(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete event');
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder(field === 'max_drawdown_pct' || field === 'days_to_recovery' ? 'asc' : 'desc');
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 70) return '#4caf50';
    if (score >= 50) return '#ff9800';
    if (score >= 30) return '#ff5722';
    return '#f44336';
  };

  const getDrawdownColor = (drawdown: number): string => {
    if (drawdown >= 0) return '#4caf50';
    if (drawdown >= -10) return '#8bc34a';
    if (drawdown >= -20) return '#ff9800';
    if (drawdown >= -30) return '#ff5722';
    return '#f44336';
  };

  const getCategoryColor = (category: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (category) {
      case 'pandemic': return 'error';
      case 'war': return 'warning';
      case 'economic': return 'primary';
      case 'political': return 'secondary';
      case 'natural_disaster': return 'info';
      default: return 'default';
    }
  };

  const selectedEvent = events.find(e => e.id === selectedEventId);

  return (
    <Box>
      {/* Event Selection and Controls */}
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Market Event</InputLabel>
                <Select
                  value={selectedEventId}
                  onChange={(e) => setSelectedEventId(e.target.value)}
                  label="Select Market Event"
                  disabled={loadingEvents}
                >
                  {events.map((event) => (
                    <MenuItem key={event.id} value={event.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={event.category} 
                          size="small" 
                          color={getCategoryColor(event.category)}
                        />
                        <span>{event.name}</span>
                        <Typography variant="caption" color="text.secondary">
                          ({event.event_date})
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={analyzing ? <CircularProgress size={16} /> : <AnalyzeIcon />}
                  onClick={handleTriggerAnalysis}
                  disabled={!selectedEventId || analyzing}
                >
                  {analyzing ? 'Analyzing...' : 'Run Analysis'}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadEventAnalysis}
                  disabled={!selectedEventId || loading}
                >
                  Refresh
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setAddDialogOpen(true)}
                >
                  Add Event
                </Button>
                
                {events.length === 0 && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handlePrepopulateEvents}
                    disabled={loading}
                  >
                    Load Historical Events
                  </Button>
                )}
                
                {selectedEvent && !selectedEvent.is_system_event && (
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteEvent(selectedEventId)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Grid>
          </Grid>
          
          {/* Event Details */}
          {selectedEvent && (
            <Box sx={{ mt: 2 }}>
              <Button
                size="small"
                onClick={() => setShowDetails(!showDetails)}
                endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                Event Details
              </Button>
              <Collapse in={showDetails}>
                <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedEvent.description || 'No description available'}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Analysis window: {selectedEvent.analysis_days_before} days before to {selectedEvent.analysis_days_after} days after
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Analysis Status */}
      {analysisStatus && (
        <Card sx={{ mb: 1 }}>
          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
            <Typography variant="subtitle2" gutterBottom>Analysis Status</Typography>
            <Grid container spacing={1}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Total Stocks</Typography>
                <Typography variant="h6">{analysisStatus.total_stocks}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Analyzed</Typography>
                <Typography variant="h6" color="success.main">{analysisStatus.analyzed}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Insufficient Data</Typography>
                <Typography variant="h6" color="warning.main">{analysisStatus.insufficient_data}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Pending</Typography>
                <Typography variant="h6" color="info.main">{analysisStatus.pending}</Typography>
              </Grid>
            </Grid>
            {analyzing && (
              <LinearProgress sx={{ mt: 2 }} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary Statistics */}
      {summary && (
        <Card sx={{ mb: 1 }}>
          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
            <Typography variant="subtitle2" gutterBottom>Event Impact Summary</Typography>
            <Grid container spacing={1}>
              <Grid item xs={6} sm={2}>
                <Typography variant="caption" color="text.secondary">Stocks Analyzed</Typography>
                <Typography variant="h6">{summary.total_analyzed}</Typography>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Typography variant="caption" color="text.secondary">Avg Drawdown</Typography>
                <Typography variant="h6" sx={{ color: getDrawdownColor(summary.avg_drawdown) }}>
                  {summary.avg_drawdown?.toFixed(1)}%
                </Typography>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Typography variant="caption" color="text.secondary">Worst Drawdown</Typography>
                <Typography variant="h6" color="error.main">
                  {summary.worst_drawdown?.toFixed(1)}%
                </Typography>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Typography variant="caption" color="text.secondary">Best Drawdown</Typography>
                <Typography variant="h6" color="success.main">
                  {summary.best_drawdown?.toFixed(1)}%
                </Typography>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Typography variant="caption" color="text.secondary">Avg Resilience</Typography>
                <Typography variant="h6" sx={{ color: getScoreColor(summary.avg_resilience) }}>
                  {summary.avg_resilience?.toFixed(0)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Typography variant="caption" color="text.secondary">Fully Recovered</Typography>
                <Typography variant="h6">
                  {summary.recovered_count} ({((summary.recovered_count / summary.total_analyzed) * 100).toFixed(0)}%)
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Results Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : impacts.length > 0 ? (
        <TableContainer component={Paper} sx={{ '& .MuiTableCell-root': { py: 0.25, px: 0.75, fontSize: '0.8rem' } }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'symbol'}
                    direction={sortField === 'symbol' ? sortOrder : 'asc'}
                    onClick={() => handleSort('symbol')}
                  >
                    Symbol
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Price Before</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'max_drawdown_pct'}
                    direction={sortField === 'max_drawdown_pct' ? sortOrder : 'asc'}
                    onClick={() => handleSort('max_drawdown_pct')}
                  >
                    Max Drop %
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Days to Bottom</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'days_to_recovery'}
                    direction={sortField === 'days_to_recovery' ? sortOrder : 'asc'}
                    onClick={() => handleSort('days_to_recovery')}
                  >
                    Recovery Days
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Recovered</TableCell>
                <TableCell align="right">Volatility</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'resilience_score'}
                    direction={sortField === 'resilience_score' ? sortOrder : 'desc'}
                    onClick={() => handleSort('resilience_score')}
                  >
                    Resilience
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'opportunity_score'}
                    direction={sortField === 'opportunity_score' ? sortOrder : 'desc'}
                    onClick={() => handleSort('opportunity_score')}
                  >
                    Opportunity
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {impacts.map((impact) => (
                <TableRow key={impact.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => navigate(`/analysis?symbol=${encodeURIComponent(impact.symbol)}`)}
                      >
                        {impact.symbol}
                      </Typography>
                      <a
                        href={`https://www.screener.in/company/${getScreenerSymbol(impact.symbol)}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center' }}
                        title="View on Screener.in"
                      >
                        <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary', '&:hover': { color: 'primary.main' } }} />
                      </a>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {impact.price_before_event?.toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      {impact.max_drawdown_pct < 0 ? (
                        <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      ) : (
                        <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      )}
                      <Typography
                        variant="body2"
                        sx={{ color: getDrawdownColor(impact.max_drawdown_pct) }}
                      >
                        {impact.max_drawdown_pct?.toFixed(1)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{impact.days_to_bottom}</TableCell>
                  <TableCell align="right">
                    {impact.days_to_recovery ?? '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={impact.recovered_to_baseline ? 'Yes' : 'No'}
                      size="small"
                      color={impact.recovered_to_baseline ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {impact.volatility_during_event?.toFixed(1)}%
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={impact.resilience_score?.toFixed(0)}
                      size="small"
                      sx={{
                        bgcolor: getScoreColor(impact.resilience_score),
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={impact.opportunity_score?.toFixed(0)}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: getScoreColor(impact.opportunity_score),
                        color: getScoreColor(impact.opportunity_score),
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : selectedEventId ? (
        <Alert severity="info">
          No analysis data available for this event. Click "Run Analysis" to analyze stocks.
        </Alert>
      ) : (
        <Alert severity="info">
          Select a market event to view stock impact analysis.
        </Alert>
      )}

      {/* Add Event Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Market Event</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Event Name"
              fullWidth
              value={newEvent.name}
              onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
              required
            />
            <TextField
              label="Event Date"
              type="date"
              fullWidth
              value={newEvent.event_date}
              onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newEvent.category}
                onChange={(e) => setNewEvent({ ...newEvent, category: e.target.value })}
                label="Category"
              >
                <MenuItem value="custom">Custom</MenuItem>
                <MenuItem value="pandemic">Pandemic</MenuItem>
                <MenuItem value="war">War</MenuItem>
                <MenuItem value="economic">Economic</MenuItem>
                <MenuItem value="political">Political</MenuItem>
                <MenuItem value="natural_disaster">Natural Disaster</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newEvent.description}
              onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddEvent}
            variant="contained"
            disabled={!newEvent.name || !newEvent.event_date}
          >
            Add Event
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventImpactTab;
